import React, { useState, useEffect } from 'react';
import './ExceptionsAndFailures.css';
import '../../styles/DataGrid.css';
import Card from '../common/Card';
import Table from '../common/Table';
import Pagination from '../common/Pagination';
import CorrectionModal from './CorrectionModal';
import DuplicateComparisonModal from './DuplicateComparisonModal';
import { auth } from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/format';

interface ExceptionItem {
  id: number;
  fileName: string;
  clientName?: string;
  propertyAddress?: string;
  utilityType?: string;
  providerName?: string;
  accountNumber?: string;
  invoiceDate?: string;
  totalAmount?: number;
  confidenceScore?: number;
  status: string;
  queueReason?: string;
  createdAt: string;
  s3Path: string;
  // Server-computed supplier-shape flag (via supplier_contracts JOIN in capture.js).
  isSupplier?: boolean;
  // Original invoice fields for duplicates
  original_invoice_id?: number;
  original_id?: number;
  original_filename?: string;
  original_s3_path?: string;
  original_invoice_date?: string;
  original_total_amount?: number;
  original_status?: string;
  original_account_number?: string;
  original_provider_name?: string;
  original_is_supplier?: boolean;
  original_created_at?: string;
}

interface ExceptionsAndFailuresProps {
  initialQueue?: 'unknown' | 'duplicate';
}

const ExceptionsAndFailures: React.FC<ExceptionsAndFailuresProps> = ({ initialQueue = 'duplicate' }) => {
  // Ensure we only use valid queue types for this component
  const validInitialQueue = initialQueue === 'unknown' ? 'unknown' : 'duplicate';
  const [activeQueue, setActiveQueue] = useState<'duplicate' | 'unknown'>(validInitialQueue);
  const [items, setItems] = useState<ExceptionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    unknown: 0,
    duplicate: 0
  });
  const [selectedItem, setSelectedItem] = useState<ExceptionItem | null>(null);
  const [showCorrection, setShowCorrection] = useState(false);
  const [showDuplicateComparison, setShowDuplicateComparison] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  // 'all' means no client filter. Otherwise holds the exact client_name to filter on.
  const [selectedClient, setSelectedClient] = useState<string>('all');
  // Distinct clients with rows in the exception queues — populated from the API
  // (data is server-paginated, so we can't derive this from the loaded page).
  const [clientOptions, setClientOptions] = useState<string[]>([]);
  // True when some rows have no client — enables the "Unknown" filter option.
  const [hasUnclassified, setHasUnclassified] = useState(false);

  // Fetch the client filter dropdown options (scoped to the exception statuses).
  const fetchClients = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/capture/clients?types=unknown,duplicate`,
        { headers: { ...auth.getAuthHeaders() } }
      );
      if (response.ok) {
        const data = await response.json();
        setClientOptions(data.clients || []);
        setHasUnclassified(!!data.hasUnclassified);
      }
    } catch (error) {
      console.error('Error fetching client list:', error);
    }
  };

  // Fetch statistics (tab counts reflect the active client filter).
  const fetchStats = async (client: string = selectedClient) => {
    try {
      const params = new URLSearchParams();
      if (client && client !== 'all') params.set('client', client);
      const qs = params.toString();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/capture/stats${qs ? `?${qs}` : ''}`,
        { headers: { ...auth.getAuthHeaders() } }
      );
      if (response.ok) {
        const data = await response.json();
        setStats({
          unknown: data.unknown,
          duplicate: data.duplicate
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch queue items with pagination (and the active client filter).
  const fetchQueueItems = async (
    queueType: string,
    page: number = currentPage,
    rows: number = rowsPerPage,
    client: string = selectedClient
  ) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: rows.toString()
      });
      if (client && client !== 'all') params.set('client', client);

      const endpoint = `${process.env.REACT_APP_API_URL}/api/capture/queue/${queueType}?${params}`;

      const response = await fetch(endpoint, {
        headers: { ...auth.getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data.items);
        setTotalItems(data.totalCount);
      }
    } catch (error) {
      console.error('Error fetching queue items:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Keep tab counts in sync with the client filter.
  useEffect(() => {
    fetchStats(selectedClient);
  }, [selectedClient]);

  // Handle initialQueue prop changes
  useEffect(() => {
    const validQueue = initialQueue === 'unknown' ? 'unknown' : 'duplicate';
    if (validQueue !== activeQueue) {
      setActiveQueue(validQueue);
    }
  }, [initialQueue]);

  // Reset to first page when the queue or client filter changes.
  useEffect(() => {
    setCurrentPage(1);
    fetchQueueItems(activeQueue, 1, rowsPerPage, selectedClient);
  }, [activeQueue, selectedClient]);

  useEffect(() => {
    fetchQueueItems(activeQueue, currentPage, rowsPerPage, selectedClient);
  }, [currentPage, rowsPerPage]);

  const handleItemClick = (item: ExceptionItem) => {
    setSelectedItem(item);
    if (activeQueue === 'duplicate') {
      setShowDuplicateComparison(true);
    } else {
      setShowCorrection(true);
    }
  };

  const handleConfirmDuplicate = () => {
    setShowDuplicateComparison(false);
    setSelectedItem(null);
    fetchQueueItems(activeQueue);
    fetchStats();
  };

  const handleIgnoreDuplicate = () => {
    setShowDuplicateComparison(false);
    setShowCorrection(true);
  };

  const handleCorrectionClose = () => {
    setShowCorrection(false);
    setSelectedItem(null);
    fetchQueueItems(activeQueue);
    fetchStats();
  };

  const getStatusBadge = (status: string) => {
    let color = '#6b7280';
    let label = status;
    
    switch(status) {
      case 'unknown':
        color = '#8b5cf6';
        label = 'Unknown Provider';
        break;
      case 'duplicate':
        color = '#ec4899';
        label = 'Duplicate';
        break;
      case 'failed':
        color = '#ef4444';
        label = 'Failed';
        break;
    }
    
    return (
      <span className="status-badge" style={{ backgroundColor: color }}>
        {label}
      </span>
    );
  };

  const getColumns = () => {
    if (activeQueue === 'duplicate') {
      return [
        { key: 'client',         title: 'Client',                   width: '15%' },
        { key: 'property',       title: 'Property',                 width: '15%' },
        { key: 'utility',        title: 'Utility',                  width: '15%' },
        { key: 'invoice',        title: 'Invoice Details',          width: '15%' },
        { key: 'uploaded',       title: 'Uploaded On',              width: '8%' },
        { key: 'original',       title: 'Original Invoice Details', width: '15%' },
        { key: 'originalStatus', title: 'Original Status',          width: '9%' },
        { key: 'action',         title: 'Action',                   width: '8%' }
      ];
    } else {
      return [
        { key: 'invoice', title: 'Invoice Details',  width: '20%' },
        { key: 'client',  title: 'Client & Property', width: '20%' },
        { key: 'provider', title: 'Provider',         width: '20%' },
        { key: 'date',    title: 'Date Uploaded',    width: '20%' },
        { key: 'status',  title: 'Status',           width: '20%' }
      ];
    }
  };

  // Emoji icon per utility type — matches the pattern used in ProcessingQueues and Picklist.
  const getUtilityIcon = (type?: string) => {
    const icons: Record<string, string> = {
      'Electric': '⚡',
      'Gas': '🔥',
      'Water': '💧',
      'CWS': '❄️'
    };
    return (type && icons[type]) || '📄';
  };

  // Supplier-shape detection: use the server-provided isSupplier flag (computed in
  // capture.js via a supplier_contracts JOIN). Never derive from provider_name suffix.
  const isSupplierItem = (item: ExceptionItem) => !!item.isSupplier;

  const transformData = () => {
    if (activeQueue === 'duplicate') {
      return items.map(item => ({
        client: (
          <div className="data-grid-info">
            <div className="data-grid-name">{item.clientName || 'Unknown'}</div>
          </div>
        ),
        property: (
          <div className="data-grid-info">
            {isSupplierItem(item) ? (
              <div className="data-grid-name supplier-row-label">Supplier Contract</div>
            ) : (
              <div className="data-grid-name">{item.propertyAddress || 'No address'}</div>
            )}
          </div>
        ),
        utility: (
          <div className="data-grid-provider-cell">
            <span className="data-grid-utility-icon">{getUtilityIcon(item.utilityType)}</span>
            <span className="data-grid-provider">{item.providerName || item.utilityType || 'Unknown'}</span>
          </div>
        ),
        invoice: (
          <div className="data-grid-info">
            <div className="data-grid-name">{item.fileName}</div>
            <div className="data-grid-address">
              {item.invoiceDate && `Date: ${formatDate(item.invoiceDate)}`}
              {item.totalAmount ? ` • ${formatCurrency(item.totalAmount)}` : ''}
            </div>
            {item.accountNumber && <div className="data-grid-address">Account: {item.accountNumber}</div>}
          </div>
        ),
        uploaded: (
          <span className="date">
            {formatDate(item.createdAt)}
          </span>
        ),
        original: (
          <div className="data-grid-info">
            <div className="data-grid-name">{item.original_filename || 'N/A'}</div>
            <div className="data-grid-address">
              {item.original_invoice_date && `Date: ${formatDate(item.original_invoice_date)}`}
              {item.original_total_amount ? ` • ${formatCurrency(item.original_total_amount)}` : ''}
            </div>
            {item.original_account_number && <div className="data-grid-address">Account: {item.original_account_number}</div>}
          </div>
        ),
        originalStatus: (
          <div style={{ textAlign: 'center' }}>
            {item.original_status ? getStatusBadge(item.original_status) : '-'}
          </div>
        ),
        action: (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <button
              className="action-icon-btn view"
              onClick={(e) => {
                e.stopPropagation();
                handleItemClick(item);
              }}
              title="View Invoice"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            </button>
          </div>
        )
      }));
    } else {
      return items.map(item => ({
        invoice: (
          <div className="data-grid-info">
            <div className="data-grid-name">{item.fileName}</div>
            {item.accountNumber && <div className="data-grid-address">Account: {item.accountNumber}</div>}
          </div>
        ),
        client: (
          <div className="data-grid-info">
            <div className="data-grid-name">{item.clientName || 'Unknown'}</div>
            {isSupplierItem(item) ? (
              <div className="data-grid-address supplier-row-label">Supplier Contract</div>
            ) : (
              <div className="data-grid-address">{item.propertyAddress || 'No address'}</div>
            )}
          </div>
        ),
        provider: (
          <div className="data-grid-provider-cell">
            <span className="data-grid-utility-icon">{getUtilityIcon(item.utilityType)}</span>
            <span className="data-grid-provider">{item.providerName || item.utilityType || 'Unknown Provider'}</span>
          </div>
        ),
        date: (
          <span className="date">
            {formatDate(item.createdAt)}
          </span>
        ),
        status: getStatusBadge(item.status)
      }));
    }
  };

  return (
    <div className="exceptions-failures">
      <Card className="data-grid-table">
        <div className="data-grid-navigation">
          <div className="data-grid-tabs">
            <button
              className={`data-grid-tab ${activeQueue === 'duplicate' ? 'active' : ''}`}
              onClick={() => setActiveQueue('duplicate')}
            >
              Duplicates <span className="count">{stats.duplicate}</span>
            </button>
            <button
              className={`data-grid-tab ${activeQueue === 'unknown' ? 'active' : ''}`}
              onClick={() => setActiveQueue('unknown')}
            >
              Unknown Accounts <span className="count">{stats.unknown}</span>
            </button>
          </div>
          <div className="data-grid-actions">
            <div className="data-grid-client-filter">
              <label htmlFor="capture-exceptions-client-filter">Client</label>
              <select
                id="capture-exceptions-client-filter"
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                disabled={loading}
                aria-label="Filter exception queues by client"
              >
                <option value="all">All clients</option>
                {hasUnclassified && <option value="__unknown__">Unknown</option>}
                {clientOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className="data-grid-rows-control">
              <span>Show</span>
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
              >
                <option value={10}>10 rows</option>
                <option value={25}>25 rows</option>
                <option value={50}>50 rows</option>
                <option value={100}>100 rows</option>
              </select>
            </div>
          </div>
        </div>

        <div className="data-grid-body">

          {loading ? (
            <div className="data-grid-loading">Loading items...</div>
          ) : items.length > 0 ? (
            <>
              <Table
                columns={getColumns()}
                data={transformData()}
                onRowClick={activeQueue === 'duplicate' ? undefined : (_, index) => handleItemClick(items[index])}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalItems / rowsPerPage)}
                rowsPerPage={rowsPerPage}
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                onRowsPerPageChange={setRowsPerPage}
              />
            </>
          ) : (
            <div className="data-grid-empty">
              <p>No items in this queue</p>
            </div>
          )}
        </div>
      </Card>

      {showDuplicateComparison && selectedItem && (
        <DuplicateComparisonModal
          duplicateInvoice={{
            id: selectedItem.id,
            fileName: selectedItem.fileName,
            s3Path: selectedItem.s3Path,
            uploadedOn: selectedItem.createdAt
          }}
          originalInvoice={{
            id: selectedItem.original_id || 0,
            fileName: selectedItem.original_filename || '',
            s3Path: selectedItem.original_s3_path || '',
            uploadedOn: selectedItem.original_created_at || ''
          }}
          onClose={() => {
            setShowDuplicateComparison(false);
            setSelectedItem(null);
          }}
          onConfirmDuplicate={handleConfirmDuplicate}
          onIgnoreDuplicate={handleIgnoreDuplicate}
        />
      )}

      {showCorrection && selectedItem && selectedItem.utilityType && (
        <CorrectionModal
          invoiceId={selectedItem.id}
          utilityType={selectedItem.utilityType}
          onClose={handleCorrectionClose}
          onSave={handleCorrectionClose}
          fromDuplicateQueue={activeQueue === 'duplicate'}
        />
      )}
    </div>
  );
};

export default ExceptionsAndFailures;